<template>
  <div v-if="importedComponent">
    <v-tooltip bottom :disabled="!tooltipText">
      <template #activator="{ attrs, on }">
        <span v-on="on">
          <component
            :is="importedComponent"
            v-model="inputValue"
            :class="{ 'entsys--input-box': filterInput }"
            v-bind="[
              {
                items,
                hint,
                clearable,
                label,
                itemValue,
                itemText,
                multiple,
                persistentHint
              },
              $attrs,
              attrs
            ]"
            :menu-props="menuProperties"
            :data-test="dataTest"
            autocomplete="off"
            :search-input.sync="searchInputInherit"
            :hide-details="filterInput ? true : hideDetails"
            :placeholder="inputPlaceholder"
            outlined
            :hide-no-data="filterInput"
            v-on="inheritListeners"
            @blur="clearSearchInput"
          >
            <!--
            <template #selection="prop">
              <slot name="selection" v-bind="prop">
             
                <div class="d-inline-flex">
                  <span>
                    {{ prop.index > 0 ? ", " : "" }}{{ prop.item[itemText]
                    }}{{
                      typeof prop.item === "string" ||
                      typeof prop.item === "number"
                        ? prop.item
                        : ""
                    }}
                  </span>
                  
                  <small
                    v-else-if="!filterInput && prop.index == 1"
                    class="ml-1"
                  >
                    + {{ inputValue.length - 1 }}
                  </small>
                 
                </div>
            
              </slot>
            </template>
            -->
            <template v-if="!!$scopedSlots.selection" #selection="prop">
              <slot name="selection" v-bind="prop" />
            </template>
            <template v-if="!!$scopedSlots.item" #item="prop">
              <slot name="item" v-bind="prop" />
            </template>
            <template #append>
              <v-icon
                :style="
                  inputAppendIcon != '$dropdown'
                    ? 'margin-top: 3px;margin-right: 3px;font-size: 18px;'
                    : ''
                "
                :color="inputAppendIcon != '$dropdown' ? 'primary' : 'grey'"
                >{{ inputAppendIcon }}</v-icon
              >
            </template>
            <template #append-outer>
              <slot name="append-outer" />
            </template>
          </component>
        </span>
      </template>
      <span>
        {{ tooltipText }}
      </span>
    </v-tooltip>
  </div>
</template>
<script>
//
import { camelCase, upperFirst } from "lodash-es";
export default {
  name: "AppGenericInput",
  props: {
    componentName: {
      validator: function (value) {
        return (
          [
            "v-combobox",
            "v-autocomplete",
            "v-select",
            "v-text-field",
            "app-select-checkbox"
          ].indexOf(value) !== -1
        );
      },
      required: true
    },
    value: {
      type: [Array, String, Object, Boolean, Number],
      default: null
    },
    inheritListeners: {
      type: Object,
      default: undefined
    },
    dataTest: {
      type: String,
      default: undefined
    },
    filterInput: {
      type: Boolean,
      default: false
    },
    itemValue: {
      type: String,
      default: undefined
    },
    itemText: {
      type: String,
      default: undefined
    },
    items: {
      type: Array,
      default: () => []
    },
    label: {
      type: String,
      default: undefined
    },
    hint: {
      type: String,
      default: undefined
    },
    persistentHint: {
      type: Boolean,
      default: false
    },
    clearable: {
      type: Boolean,
      default: true
    },
    multiple: {
      type: Boolean,
      default: false
    },
    menuProps: {
      type: Object,
      default: () => {}
    },
    appendIcon: {
      type: String,
      default: undefined
    },
    placeholder: {
      type: String,
      default: undefined
    },
    searchInput: {
      type: String,
      default: undefined
    },
    hideDetails: {
      type: [String, Boolean],
      default: false
    },
    tooltipText: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      importedComponent: null,
      localizedSearchInput: null
    };
  },
  computed: {
    searchInputInherit: {
      get() {
        return this.searchInput || this.localizedSearchInput;
      },
      set(v) {
        if (this.searchInput === undefined) {
          this.localizedSearchInput = v;
          return;
        }
        this.$emit("update:search-input", v);
      }
    },
    inputValue: {
      get() {
        if (this.value == null && this.multiple) {
          return [];
        }
        return this.value;
      },
      set(v) {
        if (!v && this.multiple) {
          this.$emit("input", []);
        } else {
          this.$emit("input", v);
        }
      }
    },
    menuProperties() {
      const defaultValues = {
        //minWidth: 250,
        //maxWidth: "100%"
      };
      if (this.componentName == "v-select") {
        defaultValues.nudgeBottom = 48;
      }
      return Object.assign(defaultValues, this.menuProps);
    },
    inputAppendIcon() {
      if (this.appendIcon) return this.appendIcon;
      switch (this.componentName) {
        case "v-autocomplete":
        case "v-combobox":
          return "$iconSearch";
        case "v-select":
          return undefined;
        default:
          return undefined;
      }
    },
    inputPlaceholder() {
      if (this.placeholder) return this.placeholder;
      return undefined;
      //return this.$t("common:all.filter.label", "All");
    }
  },
  watch: {
    componentName() {
      this._importComponent();
    }
  },
  async mounted() {
    this._importComponent();
  },
  methods: {
    async _importComponent() {
      if (!this.componentName) return false;
      let componentName = upperFirst(camelCase(this.componentName));
      if (componentName == "AppSelectCheckbox") {
        const { default: comp } = await import(
          /* webpackChunkName: "generic-input" */
          /* webpackMode: "lazy" */
          /* webpackPrefetch: true */
          `@AppSelects/AppSelectCheckboxes.vue`
        );
        this.importedComponent = comp;
      } else {
        // needs to have .js at end or will try to import .map.js variant
        const { default: comp } = await import(
          /* webpackChunkName: "generic-input" */
          /* webpackMode: "lazy-once" */
          /* webpackPrefetch: true */
          `vuetify/lib/components/${componentName}/index.js`
        );
        this.importedComponent = comp;
      }
    },
    clearSearchInput() {
      this.searchInputInherit = null;
    }
  }
};
</script>
<style scoped lang="scss">
.fipa--generic-input {
  &.theme--light {
    background: white;
  }
  &.theme--dark {
    background: black;
  }
}
</style>
